import { FetchResult, DashboardFilters, APISchemaUpdate } from 'src/types/TApiQueries';
import { config } from 'src/utils/config';
import { getStandardHeaders, getUploadHeaders, performFetchWithLogging } from '../utils/helpers';
import { isErrorResponseResult } from '../utils/errorHandlers';
import { filter } from 'lodash';

export async function getSearch(filtersData: DashboardFilters, accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/search`;
  const { page, hash, ...filters } = filtersData; // do not send unneeded properties
  
  if (filters?.file_name) {
    let inputValue = filters?.file_name;

    while (inputValue.endsWith('*')) {
      inputValue = inputValue.slice(0, -1);
    }
    // Append a single asterisk if the input is not empty
    const formattedValue = inputValue ? `${inputValue}*` : '';
    filters.file_name = formattedValue;
  }
  delete filters?.["filters"]
  delete filters["face"]
  delete filters["duplicates_of"]

  if (filters?.content_type) {
    let contentType = filters?.content_type;
    if (filters?.custom_content_input !== '') {
      contentType = filters?.custom_content_input || '';
    }

    const formattedField = contentType ? `${contentType}*` : '*';

    filters.content_type = formattedField;
  }
  delete filters?.custom_content_input;

  // Only include addons_criteria if it has values
  if (filters?.addons_criteria && filters.addons_criteria.length === 0) {
    delete filters.addons_criteria;
  }

  const payload = JSON.stringify(filters);

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: payload,
  });
}

export async function getSearchByHash(
  { hash, page }: DashboardFilters,
  accessToken: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/search/${hash}/${page}`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

// The search API caps both `limit_per_page` and a useful `ids` batch at 30.
const SEARCH_IDS_BATCH = 30;

/**
 * "Find duplicates" mode: one page of exact copies of an asset (same file
 * checksum), shaped like a search response so the dashboard grid, selection,
 * paging and bulk actions work unchanged. The asset itself is never in the
 * list (the dashboard shows it in the search box instead).
 *
 * The duplicates endpoint returns ids only (30 per page, page number in the
 * path), so the cards' data comes from a search with the `ids` filter.
 */
export async function getAssetDuplicates(
  filtersData: DashboardFilters,
  accessToken: string,
): Promise<FetchResult<any>> {
  const assetId = filtersData.duplicates_of as string;
  const page = filtersData.page || 1;
  const duplicates: any = await performFetchWithLogging(
    `${config.hostUrl}/api/v1/assets/${assetId}/content/original/duplicates/${page}`,
    {
      method: 'GET',
      headers: getStandardHeaders(accessToken),
      credentials: 'include',
    },
  );
  if (isErrorResponseResult(duplicates)) return duplicates;

  const ids: string[] = duplicates.duplicates ?? [];

  const batches: string[][] = [];
  for (let i = 0; i < ids.length; i += SEARCH_IDS_BATCH) {
    batches.push(ids.slice(i, i + SEARCH_IDS_BATCH));
  }
  const searches: any[] = await Promise.all(
    batches.map((batch) =>
      performFetchWithLogging(`${config.hostUrl}/api/v1/assets/search`, {
        method: 'POST',
        headers: getStandardHeaders(accessToken),
        credentials: 'include',
        body: JSON.stringify({
          ids: batch,
          limit_per_page: SEARCH_IDS_BATCH,
          extended_result: true,
          trashed: false,
        }),
      }),
    ),
  );
  const failed = searches.find((r) => isErrorResponseResult(r));
  if (failed) return failed;

  const extendedResult: Record<string, any> = Object.assign(
    {},
    ...searches.map((r) => r.extended_result ?? {}),
  );

  return {
    status: 'OK',
    total_files: duplicates.total_files ?? 0,
    total_pages: duplicates.total_pages ?? 1,
    page,
    // Not a reusable search: paging goes back through this function.
    search_result_id: '',
    // The endpoint's order (newest first). An id the search no longer returns
    // (trashed or indexed away meanwhile) is dropped.
    result: ids.filter((id) => extendedResult[id]),
    extended_result: extendedResult,
    duplicates_of: { id: assetId, name: duplicates.name ?? '' },
  };
}

// Function to transform the response
function transformResponse(response: any): any {
  return {
    status: response.status || "OK",
    version: response.version || 1,
    total_files: response.hits || 0,
    search_result_id: response.search_id || "",
    page: response.page || 1,
    total_pages: Math.ceil((response.hits || 0) / (response.limit_per_page || 1)),
    result: response.data?.matches || [],
    extended_result: response.data?.extended_result || {},
  };
}

export async function getFaceSearch(filtersData: any, accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/ml/people/facesearch`;
  const { face, limit_per_page = 30 } = filtersData; // Only required data



  // Create FormData object to send multipart/form-data
  const formData = new FormData();
  formData.append('save_search', 'true');
  formData.append('include_groups', 'false');
  formData.append('extended_result', 'true');
  formData.append('strictness', '9.9');
  formData.append('limit_per_page', limit_per_page.toString());
  // Check if filters exist and append as a JSON string
  if (filtersData?.filters) {
    formData.append('filters', JSON.stringify(filtersData.filters));
  }

  // Function to convert a base64 string to Blob
  function base64ToBlob(base64: string, contentType = '', sliceSize = 512): Blob {
    const byteCharacters = atob(base64); // Decode base64 string
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i); // Get char codes of slice
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray); // Collect into byte array
    }

    return new Blob(byteArrays, { type: contentType });
  }

  // Assuming the image data comes as 'data:image/jpeg;base64,...'
  const base64Data = face.split(',')[1]; // Base64 image data without the prefix
  const contentType = face.split(',')[0].split(':')[1].split(';')[0]; // Get content type (e.g., 'image/jpeg')

  // Convert base64 to Blob and append to FormData as binary
  const imageBlob = base64ToBlob(base64Data, contentType);
  formData.append('image', imageBlob, 'image.jpg'); // Add image file to form


  // Perform fetch request and transform response before returning
  const rawResponse = await performFetchWithLogging(url, {
    method: 'POST',
    headers: {
      Authorization: accessToken,
      'X-FileSpin-Client': 'filespin-ui',
    },
    credentials: 'include',
    body: formData,
  });

  // Transform the response before returning
  return transformResponse(rawResponse);

}



export async function getFaceSearchByHash(
  { hash, page, limit_per_page = 30  }: any,
  accessToken: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/ml/people/facesearch?page=${page}&search_id=${hash}&limit_per_page=${limit_per_page}&extended_result=true`;

  const rawResponse = await performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
  return transformResponse(rawResponse)
}

export async function getAssetDetails(assetId: string, accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/data`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function getAssetRelationships(assetId: string, accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/relationships`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function getAssetVersions(assetId: string, accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/versions`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

/** Makes a pending version live (release) or a previous one live again (restore). */
export async function activateAssetVersion(
  assetId: string,
  versionNo: number,
  accessToken: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/versions/${versionNo}/activate`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({}),
  });
}

export async function rejectAssetVersion(
  assetId: string,
  versionNo: number,
  accessToken: string,
  note?: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/versions/${versionNo}/reject`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(note ? { note } : {}),
  });
}

export async function updateAssetVersionData(
  assetId: string,
  versionNo: number,
  versionData: { [key: string]: any },
  accessToken: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/versions/${versionNo}`;

  return performFetchWithLogging(url, {
    method: 'PATCH',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ version_data: versionData }),
  });
}

/** Sets (ISO date) or clears (null) when a pending version is needed live. */
export async function updateAssetVersionNeededBy(
  assetId: string,
  versionNo: number,
  neededBy: string | null,
  accessToken: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/versions/${versionNo}`;

  return performFetchWithLogging(url, {
    method: 'PATCH',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ needed_by: neededBy }),
  });
}

/**
 * A short-lived private link to a non-live version's thumbnail or original.
 * Versions are never on the CDN; the API returns a presigned URL valid for a
 * few minutes.
 */
export async function getAssetVersionLink(
  assetId: string,
  versionNo: number,
  which: 'thumbnail' | 'download',
  accessToken: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/versions/${versionNo}/${which}?format=json`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function createAssetRelationship(
  assetId: string,
  targetAssetId: string,
  relationshipType: string,
  accessToken: string,
  subtype?: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/relationships`;

  const payload: { target_asset_id: string; relationship_type: string; subtype?: string } = {
    target_asset_id: targetAssetId,
    relationship_type: relationshipType,
  };
  if (subtype) {
    payload.subtype = subtype;
  }

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(payload),
  });
}

export async function getAssetMediaUrl(
  assetId: string,
  key: string,
  accessId: string,
  deliveryMode: 'display' | 'download',
  accessToken: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/get_link?key=${key}&expiry=${config.JWTExpiration}&delivery=${deliveryMode}&accessId=${accessId}`;

  return performFetchWithLogging(
    url,
    {
      method: 'GET',
      headers: getStandardHeaders(accessToken),
      credentials: 'include',
    },
    'text',
  );
}

export async function getAssetVisionTags(assetId: string, accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/addons/image_analysis`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function updateAssetMetadata(
  assetId: string,
  data: APISchemaUpdate<any>,
  accessToken: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/data/update`;
  // Internal FileSpin-managed fields (keys starting with _filespin) are not
  // user-editable; strip them before sending so they cannot be overwritten.
  // Exception: fields in the allowlist (e.g. AI autotag labels) may be
  // corrected by users, mirroring RESERVED_METADATA_FIELDS.USER_EDITABLE
  // on the backend.
  const userEditableInternalFields = ['_filespin_autotag_labels'];
  if (data?.data) {
    Object.keys(data.data).forEach((key) => {
      if (key.startsWith('_filespin') && !userEditableInternalFields.includes(key)) {
        delete (data.data as any)[key];
      }
    });
  }
  const payload:any = JSON.stringify(data);
  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: payload,
  });
}

export async function updateAssetMedia(assetId: string, data: any, accessToken: string): Promise<FetchResult<any>> {
  const form = new FormData();
  form.append('file', data.file, data.filename);

  const url = `${config.hostUrl}/api/v1/assets/${assetId}/content/original/replace`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getUploadHeaders(accessToken),
    credentials: 'include',
    body: form,
  });
}

// POST /api/v1/assets/{id}/process — re-run specific renditions of ONE asset
// (e.g. those skipped for lack of credits). 402 `insufficient_credits`
// ({required, balance}) when a credit-billed account still can't pay.
export async function processAssetConversions(
  assetId: string,
  keys: string[],
  accessToken: string,
): Promise<FetchResult<{ status: string; message: string; job_id?: number }>> {
  return performFetchWithLogging(`${config.hostUrl}/api/v1/assets/${assetId}/process`, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ keys }),
  });
}

export type ReprocessPayload = {
  ids: string[];
  conversions: string[];
  transcodes: string[];
  addons: string[];
};

export type ReprocessEstimateAsset = {
  id: string;
  content_type: string;
  applies: boolean;
  credits: number;
  items: { key: string; credits: number }[];
};

export type ReprocessEstimate = {
  billing: 'credits' | 'minutes';
  counts: { image: number; video: number; other: number };
  assets: ReprocessEstimateAsset[];
  video_credits: number;
  image_addon_credits: number;
  total: number;
  balance: number | null;
  sufficient: boolean;
  video_source_seconds: number;
  skipped: { id: string; reason: 'not_found' | 'no_access' }[];
};

// Both reprocess calls throw an Error carrying `status` and the JSON `body`
// (as bulkCutout does), so callers can tell a 402 from a 403 from a 400.
async function postReprocess(path: string, data: ReprocessPayload, accessToken: string) {
  const response = await fetch(`${config.hostUrl}/api/v1/assets/content/original/reprocess${path}`, {
    method: 'POST',
    headers: { ...getStandardHeaders(accessToken), 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });
  const text = await response.text();
  let body: any = {};
  try {
    body = text ? JSON.parse(text) : {};
  } catch {
    body = { message: text };
  }
  if (!response.ok) {
    const error: any = new Error(body?.message || body?.error || `Reprocess request failed (${response.status})`);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  return body;
}

export async function updateAssetBatchReprocess(data: ReprocessPayload, accessToken: string) {
  return postReprocess('', data, accessToken);
}

export async function estimateBatchReprocess(
  data: ReprocessPayload,
  accessToken: string,
): Promise<ReprocessEstimate> {
  return postReprocess('/estimate', data, accessToken);
}

export async function getAssetLink(
  assetId: string,
  accessId: string,
  content_key: string,
  expiry: number,
  delivery_mode: string,
  accessToken: string,
): Promise<FetchResult<any>> {

  const url = `${config.hostUrl}/api/v1/assets/${assetId}/get_link?key=${content_key}&expiry=${expiry}&delivery=${delivery_mode}&accessId=${accessId}`;

  return performFetchWithLogging(
    url,
    {
      method: 'GET',
      headers: getStandardHeaders(accessToken),
      credentials: 'include',
    },
    'text',
  );
}
 
export async function getBatchAssetLinks(
  data: any,
  accessToken: string,
  content_key: string,
  expiry: number,
  is_expiry_max: boolean,
  delivery_mode: string,
  accessId: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/get_link?key=${content_key}&expiry=${
    is_expiry_max ? 'MAX' : expiry
  }&delivery=${delivery_mode}&accessId=${accessId}`;

  const payload = JSON.stringify(data);

  return performFetchWithLogging(
    url,
    {
      method: 'POST',
      headers: { ...getStandardHeaders(accessToken) },
      credentials: 'include',
      body: payload,
    },
    'json',
  );
}

export async function getJobs(
  limit: number,
  offset: number,
  accessToken: string,
  status: string,
  start_time: string,
  end_time: string,
) {
  let url = `${config.hostUrl}/api/v1/jobs?limit=${limit}&offset=${offset}`;
  if (status !== 'ALL' && status !== null) {
    url += `&status=${status}`.replace('PROCESSING', 'IN_PROGRESS');
  }
  if (start_time !== '' && start_time !== 'YYYY-MM-DDThh:mm:ssZ') {
    url += `&start_time=${start_time}:00Z`;
  }
  if (end_time !== '' && end_time !== 'YYYY-MM-DDThh:mm:ssZ') {
    url += `&end_time=${end_time}:00Z`;
  }
  return performFetchWithLogging(
    url,
    {
      method: 'GET',
      headers: { ...getStandardHeaders(accessToken) },
      credentials: 'include',
    },
    'json',
  );
}

export async function getJobStatus(jobId: string, accessToken: string) {
  const url = `${config.hostUrl}/api/v1/jobs/${jobId}`;
  return performFetchWithLogging(
    url,
    {
      method: 'GET',
      headers: { ...getStandardHeaders(accessToken) },
      credentials: 'include',
    },
    'json',
  );
}

// ----- AI Image Edit (Fal-enhance) preview / confirm / discard -----
// The provider stays internal; callers only ever pick a friendly
// `operation`. Preview charges credits per apply and stages a temporary,
// non-persisted result. Confirm persists it (no extra charge); discard
// cleans it up (no refund). The `sequence` operation runs an ordered list
// of `params.steps` as a single pipeline (charged once).

// One step of a `sequence` operation. Mirrors the backend step shape in
// filespin/lib/imaging/steps.py (type, optional enabled flag, optional params).
export interface SequenceStep {
  type: string;
  enabled?: boolean;
  params?: { [key: string]: any };
}

export interface PhotoEditPreviewBody {
  operation: string;
  // For `sequence`, params carries `{ steps: SequenceStep[] }`; for discrete
  // operations it carries that op's params (or is omitted).
  params?: { steps?: SequenceStep[]; [key: string]: any };
}

export interface PhotoEditPreviewData {
  preview_token: string;
  preview_url: string;
  content_type: string;
  operation: string;
  source_asset_id?: string;
  credits_charged?: number;
}

export type PhotoEditPreviewResult =
  | { kind: 'inline'; data: PhotoEditPreviewData }
  | { kind: 'job'; jobId: string }
  | { kind: 'error'; error: any };

export async function photoEditPreview(
  assetId: string,
  body: PhotoEditPreviewBody,
  accessToken: string,
): Promise<PhotoEditPreviewResult> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/edit/preview`;
  const result: any = await performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(body),
  });

  // Sync ops finalise inline (200, parsed JSON). If the provider goes
  // async the helper hands back the raw 202 Response (body not parsed).
  if (typeof Response !== 'undefined' && result instanceof Response) {
    try {
      const json: any = await result.json();
      return { kind: 'job', jobId: json?.job_id };
    } catch (e) {
      return { kind: 'error', error: { message: 'Invalid preview response', status: 202 } };
    }
  }
  if (result && result.hasOwnProperty('error') && result.hasOwnProperty('status')) {
    return { kind: 'error', error: result };
  }
  return { kind: 'inline', data: result?.data };
}

// Auto-detect the Remove-object scene to prefill the UI: the main object to
// keep + suggested objects to remove, so users don't have to guess. Best-
// effort: returns empty fields on any failure. Cached per asset server-side.
export interface PhotoEditDetectResult {
  subject: string;
  removeSuggestions: string[];
}

/**
 * `preset` shapes the detection for the prompt that will consume it — for
 * `ghost_mannequin` the server never returns the mannequin as the subject nor
 * the garment among the removables (its `compose_prompt` would drop both, so
 * prefills naming them would be chips that lie). Omitted -> raw detection.
 */
export async function photoEditDetect(
  assetId: string,
  accessToken: string,
  preset?: string,
): Promise<PhotoEditDetectResult> {
  const qs = preset ? `?preset=${encodeURIComponent(preset)}` : '';
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/edit/detect${qs}`;
  try {
    const res: any = await performFetchWithLogging(
      url,
      { method: 'GET', headers: { ...getStandardHeaders(accessToken) }, credentials: 'include' },
      'json',
    );
    return {
      subject: (res?.data?.subject as string) || '',
      removeSuggestions: (res?.data?.remove_suggestions as string[]) || [],
    };
  } catch (e) {
    return { subject: '', removeSuggestions: [] };
  }
}

export interface PhotoEditConfirmBody {
  preview_token: string;
  disposition: 'conversion' | 'new_asset';
  params?: { [key: string]: any };
}

export type PhotoEditConfirmResult =
  | { kind: 'inline'; data: any; feedbackToken?: string } // 200, persisted now
  | { kind: 'job'; jobId: string; feedbackToken?: string } // 202, async background job
  | { kind: 'error'; error: any };

export async function photoEditConfirm(
  assetId: string,
  body: PhotoEditConfirmBody,
  accessToken: string,
): Promise<PhotoEditConfirmResult> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/edit/confirm`;
  const result: any = await performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(body),
  });

  // Small images finalise inline (200, parsed JSON). Large images apply
  // asynchronously: the helper hands back the raw 202 Response (body not
  // parsed) carrying the background job id.
  if (typeof Response !== 'undefined' && result instanceof Response) {
    try {
      const json: any = await result.json();
      return { kind: 'job', jobId: json?.job_id, feedbackToken: json?.data?.feedback_token };
    } catch (e) {
      return { kind: 'error', error: { message: 'Invalid confirm response', status: 202 } };
    }
  }
  if (result && result.hasOwnProperty('error') && result.hasOwnProperty('status')) {
    return { kind: 'error', error: result };
  }
  const data = result?.data ?? result;
  return { kind: 'inline', data, feedbackToken: data?.feedback_token };
}

export async function photoEditFeedback(
  assetId: string,
  body: { feedback_token: string; verdict: 'up' | 'down'; reason?: string },
  accessToken: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/edit/feedback`;
  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify(body),
  });
}

export async function photoEditDiscard(
  assetId: string,
  preview_token: string,
  accessToken: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/edit/discard`;
  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ preview_token }),
  });
}

export async function batchCDNPrefetch(data: any, accessToken: string) {
  const url = `${config.hostUrl}/api/v1/cdn/prefetch`;
  const payload = JSON.stringify(data);
  return performFetchWithLogging(
    url,
    {
      method: 'POST',
      headers: { ...getStandardHeaders(accessToken) },
      credentials: 'include',
      body: payload,
    },
    'text',
  );
}

export async function batchCDNPurge(data: any, accessToken: string) {
  const url = `${config.hostUrl}/api/v1/cdn/purge`;
  const payload = JSON.stringify(data);
  return performFetchWithLogging(
    url,
    {
      method: 'POST',
      headers: { ...getStandardHeaders(accessToken) },
      credentials: 'include',
      body: payload,
    },
    'text',
  );
}

export async function createNewAsset(
  assetId: string,
  data: any,
  accessToken: string,
  metadata: any,
  includeMetadata: boolean,
): Promise<string> {
  const form = new FormData();
  form.append('file', data.file, data.filename);
  const url = `${config.hostUrl}/api/v1/assets/new/content/original/upload`;
  const response: any = await fetch(url, {
    method: 'POST',
    headers: getUploadHeaders(accessToken),
    body: form,
  });
  // A refused upload (403 from the upload auth, 503 from the write gate) is an
  // HTML or `{error, reason}` body with no `files` — it used to surface as a
  // TypeError on `temp.files`. Throw the status so the caller can say why.
  if (!response.ok) {
    throw Object.assign(new Error(`Upload failed with ${response.status}`), { status: response.status });
  }
  const temp: any = await response.json();
  const newAssetId = temp.files[0].id;
  const metadataurl = `${config.hostUrl}/api/v1/assets/${newAssetId}/data/update`;

  let filteredMetadata: { [key: string]: any } = {};

  if (!includeMetadata) {
    filteredMetadata = {};
  } else {
    Object.keys(metadata).forEach((key) => {
      if (!key.startsWith('_filespin')) {
        filteredMetadata[key] = metadata[key];
      }
    });
  }
  const jsonBody = JSON.stringify({
    data: filteredMetadata,
    mode: 'REPLACE',
  });
  const sleep = () => new Promise((r) => setTimeout(r, 2000));
  for (let i = 0; i < 5; i++) {
    await sleep();
    const metadataresponse = await fetch(metadataurl, {
      method: 'POST',
      headers: getUploadHeaders(accessToken),
      body: jsonBody,
    });
    if (metadataresponse.ok) {
      break;
    }
  }

  // Link the edited copy back to its source: original (parent) -> new asset (child).
  // Best-effort so a relationship failure never breaks the create-new-asset flow.
  try {
    await createAssetRelationship(assetId, newAssetId, 'parent_child', accessToken, 'manual.image_edit');
  } catch (error) {
    console.error('Failed to create asset relationship for edited copy', error);
  }

  // Returned so a caller can act on the asset it just created (e.g. set its
  // access). Previously this fell off the end and returned undefined.
  return newAssetId;
}
//TODO: conversionName must be supplied from caller
/**
 * Upload the bytes for one conversion.
 *
 * `isPublic` posts to the endpoint's `/public` variant, which creates the row
 * already public. Do NOT publish by calling `setConversionPublicAccess`
 * afterwards instead: that queues `apricot.set_file_access`, which looks the
 * conversion up by name and silently `continue`s when it is not there yet — so
 * a just-uploaded conversion stayed private (verified: `cutout` conversions
 * saved with Public chosen came back `public = 0`).
 */
export async function updateAssetConversionMedia(
  assetId: string,
  conversionName: string,
  data: any,
  accessToken: string,
  isPublic = false,
): Promise<FetchResult<any>> {
  const form = new FormData();
  form.append('file', data.file, data.filename);

  const url = `${config.hostUrl}/api/v1/assets/${assetId}/content/${conversionName}${
    isPublic ? '/public' : ''
  }`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getUploadHeaders(accessToken),
    credentials: 'include',
    body: form,
  });
}

// ----- Conversion public/private toggle + user thumbnail (#932) -----

/** 202-async: queues apricot.set_file_access; poll /data until the flag flips. */
export async function setConversionPublicAccess(
  assetId: string,
  keys: string[],
  isPublic: boolean,
  accessToken: string,
): Promise<FetchResult<any>> {
  const action = isPublic ? 'set_public_access' : 'remove_public_access';
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/content/${action}`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ keys }),
  });
}

/** 202-async: queues asset.thumbnail_capture; poll /data for user_provided. */
export async function uploadAssetThumbnail(
  assetId: string,
  file: File,
  accessToken: string,
): Promise<FetchResult<any>> {
  const form = new FormData();
  form.append('file', file, file.name);

  const url = `${config.hostUrl}/api/v1/assets/${assetId}/thumbnail`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getUploadHeaders(accessToken),
    credentials: 'include',
    body: form,
  });
}

/**
 * Set storyboard frame `frame` (1-based) as the asset's thumbnail.
 * POST .../thumbnail/from_storyboard — 202-async like an upload; it lands as
 * the same user-provided fs_smart thumbnail.
 */
export async function setAssetThumbnailFromStoryboard(
  assetId: string,
  frame: number,
  accessToken: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/thumbnail/from_storyboard`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: JSON.stringify({ frame }),
  });
}

/** Revert to the automatically generated thumbnail. POST .../delete mirrors
 *  the conversion-delete convention (nginx upload locations don't route
 *  other methods). */
export async function deleteAssetThumbnail(
  assetId: string,
  accessToken: string,
): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/thumbnail/delete`;

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function purgeAssets(accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/trash/purge`;

  return performFetchWithLogging(url, {
    method: 'DELETE',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export async function deleteAsset(assetId: string, accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/${assetId}/delete`;

  const payload = JSON.stringify({ keys: ['original'] });

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: payload,
  });
}

export async function bulkDeleteAssets(assetIds: string[], accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/bulk/delete`;

  const payload = JSON.stringify({ ids: assetIds });

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: payload,
  });
}

export async function restoreAsset(assetId: string, accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/assets/trash/${assetId}/undelete`;

  const payload = JSON.stringify({ keys: ['original'] });

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: payload,
  });
}

export async function confirmBulkDelete(token: string, accessToken: string): Promise<FetchResult<any>> {
  const url = `${config.hostUrl}/api/v1/bulk-purge-assets/confirm/${token}`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export interface PendingBulkDeleteResponse {
  status: string;
  asset_count: number;
  start_time: string;
  end_time: string;
  storage_deletion: {
    enabled: boolean;
    note: string;
  };
  retention_filters_applied: boolean;
  user_email: string;
  created_at: string;
  expires_at: string;
  retention_filters?: Array<{ term: { [key: string]: string } }>;
}

export async function getPendingBulkDelete(
  token: string,
  accessToken: string
): Promise<FetchResult<PendingBulkDeleteResponse>> {
  const url = `${config.hostUrl}/api/v1/bulk-purge-assets/pending/${token}`;

  return performFetchWithLogging(url, {
    method: 'GET',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
  });
}

export interface BulkDeleteCountRequest {
  start_time: string;
  end_time: string;
  scope?: 'user' | 'group';
}

export interface BulkDeleteCountResponse {
  count: number;
  total_group_assets: number;
  max_allowed: number;
  within_limit: boolean;
}

export interface RetentionFilter {
  term: { [key: string]: string };
}

export interface BulkDeleteRequest {
  start_time: string;
  end_time: string;
  retention_filters?: RetentionFilter[];
  delete_from_storage?: boolean;
  scope?: 'user' | 'group';
}

export interface BulkDeleteResponse {
  status: string;
  message: string;
  token: string;
  expires_at: string;
  assets_to_delete: number;
  storage_deletion: {
    enabled: boolean;
    permission_check: {
      has_permission: boolean;
      storage_type: string;
      message: string;
    };
  };
}

export async function getBulkDeleteCount(
  data: BulkDeleteCountRequest,
  accessToken: string
): Promise<FetchResult<BulkDeleteCountResponse>> {
  const url = `${config.hostUrl}/api/v1/bulk-purge-assets/count`;
  const payload = JSON.stringify(data);

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: payload,
  });
}

export async function requestBulkDelete(
  data: BulkDeleteRequest,
  accessToken: string
): Promise<FetchResult<BulkDeleteResponse>> {
  const url = `${config.hostUrl}/api/v1/bulk-purge-assets/request`;
  const payload = JSON.stringify(data);

  return performFetchWithLogging(url, {
    method: 'POST',
    headers: getStandardHeaders(accessToken),
    credentials: 'include',
    body: payload,
  });
}

//
